/**
 * Layer 3 of the manifest: a refusal names the capability it is about. Every
 * code the setup validators give (the template validator and the field rules
 * it runs, the compose reader and judge, the authoring, the formula checker,
 * and the server's link check) maps to a manifest entry, or to null on
 * purpose when the code is about a typo or the wire rather than a capability.
 * apps/server/src/manifest-refusals.enforcement.test.ts keeps the two tables
 * complete: a new validator code fails it until it is mapped here.
 *
 * `refusalNotBuilt` is how the harness words a refused change: "not
 * possible yet" only for an entry RecordOS does not build.
 */
import { APPROVAL_REFUSALS } from '../api';
import type { SetupDraft, SetupDraftField } from '../compose-author';
import { isValueTypeId } from '../generated/value-types';
import { isPostingPattern } from '../ledger/patterns';
import { studioLimitOf } from '../setup-draft/judge';
import type { STUDIO_LIMITS } from '../setup-draft/draft';
import type { CapabilityId } from './ids';
import { capabilityById } from './manifest';

export type RefusedProblem = { readonly code: string; readonly message: string; readonly path?: string };

const each = <T extends string>(codes: readonly T[], id: CapabilityId | null): Record<T, CapabilityId | null> =>
  Object.fromEntries(codes.map((code) => [code, id])) as Record<T, CapabilityId | null>;

/** The codes whose entry is fixed. Null means the code is about a typo or the wire, not a capability. */
export const REFUSAL_CAPABILITY: Readonly<Record<string, CapabilityId | null>> = {
  // The size of a setup.
  ...each(['RECORDS_TOO_MANY', 'RECORDS_MISSING', 'FIELDS_TOO_MANY', 'FIELDS_MISSING', 'SLOTS_FULL', 'MAIN_RECORD_MISSING', 'WORDS_TOO_MANY', 'RECORD_KIND_INVALID'], 'record.record'),
  // A list holds no typed money, and no card totals the money it works out (W11 of docs/plans/workbook-map.md).
  ...each(['DIRECTORY_HOLDS_MONEY'], 'record.list'),
  ...each(['LIST_MONEY_NOT_TOTALLED'], 'record.list_money'),
  ...each(['ICON_UNKNOWN'], 'record.icon'),
  ...each(['BINDING_FIELD_UNKNOWN', 'BINDING_PURPOSE_DUP', 'BINDING_TITLE_MISSING', 'BINDING_AMBIGUOUS', 'BINDING_EMPTY', 'PURPOSE_UNKNOWN', 'PURPOSE_TYPE_MISMATCH', 'CONST_NOT_ALLOWED', 'CONST_OUT_OF_RANGE'], 'record.bindings'),
  ...each(
    ['SEED_FIELD_CONDITIONAL', 'SEED_FIELD_UNKNOWN', 'SEED_ON_RECORD', 'SEED_REFERENCE_RING', 'SEED_REFERENCE_UNKNOWN', 'SEED_ROWS_TOO_MANY', 'SEED_VALUE_SET'],
    'record.seed_rows',
  ),
  // Fields.
  ...each(['OPTIONS_MISSING', 'OPTIONS_UNEXPECTED', 'OPTION_KEY_DUP'], 'field.type.select'),
  ...each(['OPTION_TONE', 'SIGN_FIELD_MISSING', 'SIGN_OPTION_MISSING'], 'field.option.option_sign_tone'),
  ...each(['SHAPE_UNKNOWN', 'COMPONENT_UNKNOWN', 'COMPONENT_TYPE_MISMATCH', 'PROP_TYPE', 'PROP_ENUM', 'PROP_UNKNOWN'], 'field.option.shape'),
  ...each(['REQUIRED_IN_ATTRS'], 'field.option.required'),
  ...each(['REFERENCE_TARGET_NOT_DIRECTORY', 'REFERENCE_TARGET_TITLE_NOT_TEXT', 'REFERENCE_TARGET_SELF', 'REFERENCE_TARGET_UNKNOWN', 'REFERENCE_NEEDS_TARGET'], 'field.type.reference'),
  ...each(
    ['FILTER_CYCLE', 'FILTER_EQUALS_NOT_CHOICE', 'FILTER_EQUALS_UNKNOWN', 'FILTER_FIELD_NOT_REFERENCE', 'FILTER_FIELD_UNKNOWN', 'FILTER_LIST_MISMATCH', 'FILTER_NOT_CHOICE', 'FILTER_SHAPE'],
    'field.option.filter',
  ),
  ...each(['FILTER_MAX_CHOSEN'], 'field.option.max_chosen'),
  // A number series (Phase 8 of docs/plans/workbook-map.md).
  ...each(['SERIES_SHAPE', 'SERIES_PER', 'SERIES_IN_USE', 'SERIES_NUMBER_TAKEN', 'SERIES_NUMBER_GIVEN'], 'field.type.series'),
  ...each(['ROLLUP_SHAPE', 'ROLLUP_OVER_UNKNOWN', 'ROLLUP_OVER_FIXED', 'ROLLUP_VIA', 'ROLLUP_OF', 'ROLLUP_WHERE', 'FIELD_IN_ROLLUP'], 'field.type.rollup'),
  ...each(
    [
      'LINES_COUNT_FROM',
      'LINES_FIELDS_EMPTY',
      'LINES_FIELDS_MAX',
      'LINES_KEY_RESERVED',
      'LINES_MAX_SHAPE',
      'LINES_NEVER_REQUIRED',
      'LINES_NOT_ALLOWED',
      'LINES_NO_DEFAULT',
      'LINES_ON_LIST',
      'LINES_SHAPE',
      'TARGET_NOT_ALLOWED',
    ],
    'field.type.lines',
  ),
  ...each(['LINES_START_SHAPE'], 'field.option.lines_start'),
  ...each(['STARTS_ONCE_ALONE'], 'field.option.once'),
  ...each(['FIELD_LOCKED', 'FIELD_IN_EDITABLE_WHEN'], 'field.option.editable_when'),
  ...each(['MUST_BE_SHAPE', 'MUST_BE_MESSAGE', 'FIELD_IN_MUST_BE', 'ENTRY_RULE_BROKEN'], 'record.must_be'),
  // An entry that waits for approval (Phase 11 of docs/plans/ledger.md): the decision route's and OS's card's refusals.
  ...each([...APPROVAL_REFUSALS, 'ENTRY_NEEDED'], 'lifecycle.approval'),
  ...each(['LINES_TOO_DEEP'], 'not_yet.third_lines_level'),
  // Worked-out values: the checker's own codes, and the template validator's.
  ...each(
    [
      'FORMULA_SHAPE',
      'FORMULA_TOO_BIG',
      'FORMULA_FIELD_UNKNOWN',
      'FORMULA_FIELD_COMPUTED',
      'FORMULA_FIELD_UNREADABLE',
      'FORMULA_PARENT_COMPUTED',
      'FORMULA_CYCLE',
      'FORMULA_NODE_DUPLICATE',
      'FORMULA_NODE_UNKNOWN',
      'FORMULA_NODE_UNUSED',
      'FORMULA_ROOT_UNKNOWN',
      'FORMULA_TYPE_MISMATCH',
      'FORMULA_RESULT_MISMATCH',
      'FORMULA_NEVER_REQUIRED',
      'FORMULA_SPEC_MISSING',
      'FORMULA_DEEP',
    ],
    'field.type.formula',
  ),
  ...each(['FORMULA_HOP_NOT_REFERENCE', 'FORMULA_HOP_NOT_STORED'], 'formula.piece.hop'),
  ...each(['FORMULA_AGG_NOT_HERE'], 'formula.piece.agg'),
  ...each(['FORMULA_DATE_UNIT'], 'formula.piece.date_add'),
  ...each(['FORMULA_TODAY_STORED'], 'formula.piece.today'),
  // Where fields sit.
  ...each(['LAYOUT_FIELD_TWICE', 'LAYOUT_FIELD_UNKNOWN', 'LAYOUT_NODE_UNKNOWN', 'LAYOUT_SHAPE', 'LAYOUT_TOO_BIG', 'LAYOUT_TOO_DEEP'], 'arrangement.sections'),
  // How entries are written, and the cards on top.
  ...each(
    ['CAPTURE_CELL_NOT_SELECT', 'CAPTURE_FIELD_UNKNOWN', 'CAPTURE_MODE', 'CAPTURE_NOT_OBJECT', 'CAPTURE_OVER_NOT_REFERENCE', 'CAPTURE_QUICK_UNKNOWN', 'CAPTURE_SHARED_INVALID'],
    'surface.sheet',
  ),
  ...each(['SUMMARY_CARD_UNKNOWN', 'SUMMARY_COUNT_INVALID', 'SUMMARY_TILE_NOT_OBJECT', 'TILE_FIELD_MISSING'], 'view.summary'),
  ...each(['VIEW_FIELD_UNKNOWN', 'VIEW_CONDITION_INVALID'], 'view.stored_options'),
  ...each(['SUMMARY_GROUP_BY_MISSING', 'SUMMARY_GROUP_BY_UNKNOWN'], 'insight.group_by'),
  ...each(['SUMMARY_SCOPE_IMPOSSIBLE', 'CARD_DATE_FIELD'], 'insight.scope'),
  ...each(
    [
      'TILE_CONDITION_FIELD_UNKNOWN',
      'TILE_CONDITION_FIELD_UNREADABLE',
      'TILE_CONDITION_NOT_OBJECT',
      'TILE_CONDITION_NUM_INVALID',
      'TILE_CONDITION_NUM_MISSING',
      'TILE_CONDITION_OPTION_UNKNOWN',
      'TILE_CONDITION_OP_INVALID',
      'TILE_WHERE_NOT_LIST',
    ],
    'insight.where',
  ),
  // How money is kept (step 12.6 of docs/plans/ledger.md): each code names
  // the part it is about, so OS explains a refusal the way the form does. A
  // money rule's own codes are read from the draft (REFUSAL_CAPABILITY_OF):
  // the pattern of the record the path names, or the rule itself.
  // The Accounts list (L15): a row with money in it, a type fixed once used,
  // a row with no type, and the list's own shape.
  ...each(
    [
      'ACCOUNT_IN_USE',
      'PARTY_IN_USE',
      'ACCOUNT_TYPE_LOCKED',
      'ACCOUNT_TYPE_MISSING',
      'ACCOUNTS_NOT_LIST',
      'ACCOUNTS_TYPE_FIELD',
      'ACCOUNTS_TYPE_OPTIONS',
      'ACCOUNTS_SYSTEM_KEY',
      'ACCOUNTS_SYSTEM_KEY_DUP',
      'ACCOUNTS_LIST_TWICE',
      'ACCOUNTS_SEED_ROWS',
      'ACCOUNTS_ROW_TYPE',
    ],
    'money.accounts',
  ),
  // A charge plan the setup drew on a party list that apply could not install (L23, step 12.1).
  ...each(['CHARGE_PLAN_NOT_LIST', 'CHARGE_PLAN_NOT_PARTIES', 'CHARGE_PLAN_SHAPE', 'CHARGE_PLAN_HELD'], 'money.pattern.charge_plan'),
  ...each(['CHARGE_PLAN_NO_ACCOUNTS'], 'money.accounts'),
  // An approval chain the setup wrote from words that apply could not save (L55, step 12.1).
  ...each(['APPROVAL_CHAIN_ON_LIST', 'APPROVAL_CHAIN_SHAPE'], 'lifecycle.approval'),
  // A pick its field does not offer (L16).
  ...each(['ACCOUNT_NOT_ALLOWED'], 'money.category'),
  // A party row's dates (L22, Phase 5).
  ...each(['PARTY_PERIOD_OVERLAP', 'PARTY_PERIOD_BACKWARDS'], 'money.party_period'),
  // A charge plan's shape, and a run (Phase 5): the plan the page showed has moved, or there is none.
  ...each(['CHARGE_CADENCE', 'CHARGE_LINES', 'RULE_TAKEN'], 'money.pattern.charge_plan'),
  ...each(['CHARGE_PLAN_NONE', 'CHARGE_RUN_MOVED', 'CHARGE_RUN_NOT_YET', 'CHARGE_RUN_PAUSED', 'CHARGE_RUN_NOT_PAUSED'], 'money.charge_run'),
  // Moving a payment to another charge (L21, Phase 4).
  ...each(['MATCH_AMOUNT', 'MATCH_TARGET', 'MATCH_TARGET_FULL'], 'money.match'),
  // Who may read the workspace's money (L27), and a workspace with no tax.
  ...each(['MONEY_READ_NOT_ALLOWED'], 'money.see_money'),
  ...each(['TAX_NOT_SET', 'TAX_REGIME_INVALID'], 'workspace.tax'),
  ...each(['FISCAL_YEAR_INVALID', 'MONEY_INVALID', 'MONEY_EDIT'], 'workspace.financial_year'),
  // A tax invoice agrees with what its entry counted (L49 of docs/plans/ledger.md).
  ...each(['DOC_NOT_POSTED', 'DOC_PLACE_POSTED', 'DOC_POSTING_MISMATCH'], 'document.issue'),
  ...each(['MONEY_PERIOD_INVALID', 'PACK_FILE_UNKNOWN'], null),
  // Close a month (Phase 6, L12, L17): To sort holds money, entries wait, it is closed already, or the person may not.
  ...each(['CLOSE_TO_SORT', 'CLOSE_ENTRIES_WAITING', 'CLOSE_ALREADY_CLOSED', 'CLOSE_NOT_ALLOWED'], 'money.close'),
  // A closed month stays closed on every write: the way on is Correct.
  ...each(['PERIOD_CLOSED', 'ENTRY_NOT_CLOSED', 'ENTRY_ALREADY_CORRECTED', 'ENTRY_IS_CORRECTION'], 'money.correct'),
  ...each(['CLOSE_DAY_INVALID', 'ACCOUNTANT_VIEW_INVALID'], null),
  // Task rules and the tasks they raise (step 9.7 of docs/plans/ledger.md): a rule that does not read, a record full of them, one gone.
  ...each(['TASK_RULE_INVALID', 'TASK_RULES_FULL', 'TASK_RULE_NOT_FOUND', 'TASK_NOT_FOUND'], 'lifecycle.tasks'),
  ...each(['BAD_TASK_ID'], null),
  // The setup draft's checks of Phase 9a: a task rule, the open row a reference starts with, the stock items mark.
  ...each(['TASK_RULE_SHAPE'], 'lifecycle.tasks'),
  ...each(['OPEN_ROW_SHAPE'], 'field.option.open_row'),
  ...each(['STOCK_ITEMS_SHAPE'], 'field.type.quantity'),
  // The ledger's routes: a typo, the wire, a row or a record that is not there, or a right the route checks.
  ...each(
    [
      'TENANT_REQUIRED',
      'BAD_ID',
      'BAD_BODY',
      'BAD_REQUEST',
      'BAD_RECORD_ID',
      'BAD_ENTRY_ID',
      'BAD_MONTHS',
      'NOT_A_MEMBER',
      'NOT_ALLOWED',
      'ENTRY_UNKNOWN',
      'ENTRY_MOVED',
      'RECORD_UNKNOWN',
      'RECORD_NAME_TAKEN',
      'MATCH_UNKNOWN',
      'STRUCTURE_REFUSED',
    ],
    null,
  ),
  // OS's money tools (steps 12.3 and 12.4 of docs/plans/ledger.md): each refusal names the part of money it is about,
  // so OS explains it the way the screen does. An account named that is not there, and a workspace with no Accounts list.
  ...each(['ACCOUNT_UNKNOWN', 'MONEY_NOT_KEPT'], 'money.accounts'),
  ...each(['ACCOUNTANT_VIEW_OFF'], 'money.accountant_view'),
  ...each(['PARTY_UNKNOWN', 'PARTY_AMBIGUOUS'], 'money.statement'),
  ...each(['CATEGORY_EXISTS', 'CATEGORY_NEEDED'], 'money.category'),
  ...each(['TO_SORT_EMPTY', 'ENTRY_NOT_IN_TO_SORT'], 'money.to_sort'),
  ...each(['ADJUSTMENT_UNKNOWN'], 'money.pattern.adjustment'),
  ...each(['CLOSE_WAITING'], 'money.close'),
  ...each(['CHARGE_PLAN_UNKNOWN', 'NOTHING_TO_CHARGE'], 'money.charge_run'),
  ...each(['NOTHING_TO_CORRECT'], 'money.correct'),
  ...each(['MATCH_NEEDED', 'MATCH_TARGET_INVALID', 'NOTHING_TO_MOVE'], 'money.match'),
  ...each(['APPROVAL_STEP_UNKNOWN'], 'lifecycle.approval'),
  // Which record, or an entry said wrong: the tool's problems name the field; no part of money to name.
  ...each(['RECORD_NEEDED', 'ENTRY_INVALID', 'DRAFT_SHAPE'], null),
  // The workspace.
  ...each(['CURRENCY_MISSING', 'CURRENCY_UNKNOWN', 'CURRENCY_NOT_REQUIRED_BUT_MONEY', 'CURRENCY_REQUIRED_BUT_NO_MONEY'], 'workspace.currency'),
  // A typo, the wire, or a template's own bookkeeping: no capability to name.
  ...each(
    [
      'BUSINESS_NAME_INVALID',
      'SETUP_NOT_OBJECT',
      'SPEC_INVALID',
      'SPEC_NOT_OBJECT',
      'SPEC_ID_NOT_COMPOSED',
      'SPEC_VERSION_NOT_ONE',
      'SPEC_UNEXPECTED',
      'WORDS_INVALID',
      'WORD_INVALID',
      'WORD_KEY_INVALID',
      'WORD_SHADOWS_CATALOGUE',
      'LABEL_KEY_MISSING',
      'LABEL_KEY_UNKNOWN',
      'NOUN_KEY_NOT_SHIPPED',
      // A link left in a studio draft (step 9.5 of docs/plans/ledger.md): Links no longer run, so no entry.
      'LINK_RETIRED',
      'FIELD_UNKNOWN',
      'TARGET_UNKNOWN',
      'RECORD_KEY_DUP',
      'RECORD_KEY_INVALID',
      'RECORD_NOT_OBJECT',
      'FIELD_KEY_DUP',
      'FIELD_LABEL_DUP',
      'FIELD_KEY_INVALID',
      'FIELD_NOT_OBJECT',
      'BINDING_NOT_OBJECT',
      'TIER_INVALID',
      'TIER_NOT_CUSTOM',
      'ORD_INVALID',
      'ORD_TAKEN',
      'VIEW_MISSING',
      'VIEW_NOT_OBJECT',
      'VIEW_ORD_TAKEN',
      'VIEW_TYPE_INVALID',
      'ATTR_KEY_INVALID',
      'ATTR_KEY_MISSING',
      'ATTR_KEY_TAKEN',
      'SLOT_MISSING',
      'SLOT_TAKEN',
      'SLOT_UNKNOWN',
      'SLOT_FAMILY',
      'STORAGE_TARGET',
      'SAMPLE_COUNT',
      'SAMPLE_FIELD_UNKNOWN',
      'ANSWER_TAGS_INVALID',
      'ANSWER_TAG_UNKNOWN',
      'KEY_BY_INVALID',
      'RECORD_WHEN_ANSWER_NOT_DIRECTORY',
      'SEED_WHEN_NOT_OBJECT',
      'SEED_WHEN_VALUES_MISSING',
      'WHEN_ANSWER_ON_BOUND',
      'WHEN_ANSWER_ON_CAPTURE',
      'WHEN_ANSWER_ON_REQUIRED',
      'WHEN_ANSWER_ON_VIEW_FIELD',
      'WHEN_ANSWER_PAIR_BROKEN',
      'LAYOUT_VERSION_NEW',
    ],
    null,
  ),
};

/* ----------------------------------------------- codes read from the problem */

const fieldsDeep = (fields: readonly SetupDraftField[]): SetupDraftField[] =>
  fields.flatMap((field) => [field, ...fieldsDeep(field.lines?.fields ?? [])]);

/** The field a problem's path names in the draft: `composed.records[sales].fields[worked]`, lines included. */
const fieldOf = (problem: RefusedProblem, draft: SetupDraft | undefined): SetupDraftField | undefined => {
  const path = problem.path ?? '';
  const recordKey = /\.records\[([^\]]+)\]/.exec(path)?.[1];
  const fieldKeys = [...path.matchAll(/fields\[([^\]]+)\]/g)].map((match) => match[1]!);
  const fieldKey = fieldKeys.at(-1);
  if (recordKey === undefined || fieldKey === undefined) return undefined;
  const record = draft?.records.find((one) => one.key === recordKey);
  return record === undefined ? undefined : fieldsDeep(record.fields).find((field) => field.key === fieldKey);
};

/** The record a problem's path names in the draft: `spec.records[sales].posting`. */
const recordOf = (problem: RefusedProblem, draft: SetupDraft | undefined) => {
  const key = /records\[([^\]]+)\]/.exec(problem.path ?? '')?.[1];
  return key === undefined ? undefined : draft?.records.find((record) => record.key === key);
};

/**
 * A money rule's refusal: the pattern of the rule the path names (the choice
 * the path names, for a rule that picks by a choice), whose words say what
 * it reads; else the rule itself (a save on the server, where no draft is).
 */
const ruleEntry = (problem: RefusedProblem, draft?: SetupDraft): CapabilityId => {
  const rule = recordOf(problem, draft)?.posting;
  const option = /byChoice\[([^\]]+)\]/.exec(problem.path ?? '')?.[1];
  const pattern = (option === undefined ? undefined : rule?.byChoice?.options[option]?.pattern) ?? rule?.pattern;
  return pattern !== undefined && isPostingPattern(pattern) ? `money.pattern.${pattern}` : 'money.rule';
};

/** The codes a money rule gives (patterns.ts, spec/posting.ts, ledger/post.ts). */
const RULE_CODES = [
  'RULE_ROLE_MISSING',
  'RULE_ROLE_UNKNOWN',
  'RULE_ACCOUNT_UNKNOWN',
  'RULE_CHOICE_UNMAPPED',
  'RULE_CHOICE_UNKNOWN',
  'RULE_PARTY_UNKNOWN',
  'RULE_FIELD_TYPE',
  'RULE_FIELD_UNKNOWN',
  'RULE_ON_LIST',
  'POSTING_UNBALANCED',
  'POSTING_ONE_LINE',
] as const;

const typeEntry = (problem: RefusedProblem, draft?: SetupDraft): CapabilityId | null => {
  const type = fieldOf(problem, draft)?.type as string | undefined;
  return type !== undefined && isValueTypeId(type) ? `field.type.${type}` : null;
};

/** The codes whose entry depends on the problem: what its path names in the draft. */
/** The capability each ceiling of the studio draft is about. */
const STUDIO_LIMIT_CAPABILITY: Partial<Record<keyof typeof STUDIO_LIMITS, CapabilityId>> = {
  records: 'record.record',
  fields: 'record.record',
  seedRows: 'record.list',
  boardCards: 'insight.dashboard',
  roles: 'people.role',
  rolePeople: 'people.role',
  imports: 'input.import',
};

export const REFUSAL_CAPABILITY_OF: Readonly<Record<string, (problem: RefusedProblem, draft?: SetupDraft) => CapabilityId | null>> = {
  ...Object.fromEntries(RULE_CODES.map((code) => [code, ruleEntry])),
  // A pattern no rule may name is about the rule, never a pattern.
  RULE_PATTERN_UNKNOWN: () => 'money.rule',
  // Not a refusal: the warning a draft L58 kept says on its save, about its record's rule.
  MONEY_RULE_HELD: () => 'money.rule',
  // A field of a type no setup places (rollup, lookup): the registry's storage rule refuses it.
  STORAGE_MISMATCH: typeEntry,
  VALUE_TYPE_UNKNOWN: typeEntry,
  // The studio judge's ceilings (setup-draft/judge.ts): the message names which.
  LIMIT: (problem) => {
    const limit = studioLimitOf(problem);
    return limit === null ? null : (STUDIO_LIMIT_CAPABILITY[limit] ?? null);
  },
};

/** The entry a problem is about, or null when it is about none. */
export const refusalCapability = (problem: RefusedProblem, draft?: SetupDraft): CapabilityId | null => {
  const of = REFUSAL_CAPABILITY_OF[problem.code];
  if (of !== undefined) return of(problem, draft);
  return REFUSAL_CAPABILITY[problem.code] ?? null;
};

/**
 * Whether a refusal is about something RecordOS does not build yet (its
 * entry is not shipped), something it builds once the change is made another
 * way (a shipped entry: a link that fills a field, a field of a type), or
 * names no entry (null). Never words: the person reads "not possible" only
 * for an entry not built (D.2 of docs/plans/links-fixes.md, 17 Oct: "not
 * possible: see link.fill.field" on a link that three named fixes would draw).
 */
export const refusalNotBuilt = (problem: RefusedProblem, draft?: SetupDraft): boolean | null => {
  const id = refusalCapability(problem, draft);
  const entry = id === null ? null : capabilityById(id);
  return entry === null ? null : entry.status !== 'shipped';
};
